import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { ActionButton } from 'app/components/Button/ActionButton';
import { FormInput } from 'app/components/Input/FormInput';
import { RadioInput } from 'app/components/Input/RadioInput';
import ConfirmationModal from 'app/components/Modal/ConfirmationModal';
import { FormSelect } from 'app/components/Select/FormSelect';
import { useListView } from 'app/components/Table/core/ListViewProvider';
import { useQueryRequest } from 'app/components/Table/core/QueryRequestProvider';
import { useQueryResponse } from 'app/components/Table/core/QueryResponseProvider';
import { emptyListViewProvider } from 'app/components/Table/core/constants';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import { useAuth } from 'app/modules/auth';
import { getItems } from 'app/modules/inventory/items/core/requests';
import { useRackStore } from 'app/modules/inventory/racks/store';
import { useChangeParams } from 'app/modules/query-state/hooks';
import { getSensors } from 'app/modules/system-settings/devices/core/requests';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import { getResponseError } from 'app/helpers/errors';
import { useNumber } from 'app/hooks';
import { usePolicyInUser } from 'app/hooks/policy';
import { isNull, isUndefined } from 'lodash';

import { RACK_TYPE } from '../core/constants';
import { RackType } from '../core/enum';
import { createRack, getRack, updateRack } from '../core/requests';
import { Rack, RackFormType, RackRequest, initialRackRequest } from '../core/type';
import { useFormik } from 'formik';
import { useOrganizationStore } from 'theme/layout/components/aside/store';
import * as Yup from 'yup';

const WardCard = styled.div`
  width: 100%;
  /* padding-left: 8px; */
`;

const ItemType = styled.div`
  font-weight: 700;
  font-size: 14px;
  line-height: 23px;
  color: var(--gray-700);
  margin-bottom: 4px;
`;

interface Props {
  onClickCloseModal?: (newRack?: Rack) => void;
  itemRack?: Rack;
  wards: Ward[];
  isAddRackForWard?: boolean;
  isEditLayoutRack?: boolean;
  setRefetchTimestamp?: (value: number) => void;
}
const FormRackModal: React.FC<Props> = ({
  itemRack = null,
  wards,
  onClickCloseModal,
  isAddRackForWard = false,
  isEditLayoutRack = false,
  setRefetchTimestamp,
}) => {
  const { updateState, state } = useQueryRequest();
  const { setItemForUpdate } = useListView();
  const { refetch } = useQueryResponse();
  const { onChangeParams } = useChangeParams();
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [formRequest] = useState<RackRequest>({
    name: itemRack ? itemRack.name : initialRackRequest.name,
    code: itemRack ? itemRack.code : initialRackRequest.code,
    info: itemRack ? itemRack.info : initialRackRequest.info,
    row: itemRack ? itemRack.row : initialRackRequest.row,
    col: itemRack ? itemRack.col : initialRackRequest.col,
    // binCount: itemRack ? itemRack.binCount : initialRackRequest.binCount,
    isCustom: itemRack ? itemRack.isCustom : true,
  });
  const wardId = useNumber(0);
  const hasItemAssigned = itemRack?.items ? itemRack.items > 0 : false;
  const rackType = useNumber(
    itemRack && itemRack.isCustom ? RackType['Custom rack'] : RackType['Custom rack'],
  );

  const typeCustom = rackType.value === RackType['Custom rack'];

  const currentUser = useAuth().currentUser;
  const permission = usePolicyInUser(currentUser?.policy ?? []);
  const isSystemAdminCurrent = permission.systemAdmin;
  const organization = useOrganizationStore((s) => s.state.organization);

  const params = useParams<{ rackId: string; orgId: string }>();
  const orgId = params.orgId ? Number(params.orgId) : organization?.id;

  useEffect(() => {
    if (itemRack?.location) {
      wardId.setValue(Number(itemRack.location.id));
    }
  }, [itemRack]);

  useEffect(() => {
    if (isAddRackForWard) {
      wardId.setValue(Number(wards[0].id));
    }
  }, [isAddRackForWard]);

  const fetchItems = async (search?: string) => {
    try {
      const res = await getItems(
        `page=1&limit=999999999&orderBy=createdAt&orderDirection=DESC&status=1&search=${
          search ?? ''
        }${isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''}`,
      );

      useRackStore.getState().setState({ items: res?.data?.items ?? [] });
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSensors = async (search?: string) => {
    try {
      const resSensors = await getSensors(
        `page=1&limit=999999999&orderBy=createdAt&orderDirection=DESC&search=${search ?? ''}${
          isSystemAdminCurrent ? `&orgId=${organization?.id}` : ''
        }`,
      );

      const sensorsNotAssign = resSensors?.data?.items?.filter(
        (sensor) => isNull(sensor.inventoryLocation?.bin) || isNull(sensor.inventoryLocation),
      );

      useRackStore.getState().setState({ sensors: sensorsNotAssign ?? [] });
    } catch (error) {
      console.log(error);
    }
  };

  const formik = useFormik({
    initialValues: formRequest,
    validationSchema: Yup.object().shape({
      name: Yup.string().trim().required('Name is required'),

      row: Yup.number()
        .min(1, 'Row must be at least 1')
        .required('Row is required')
        .test('max-multiply', 'Row × Column cannot be greater than 1000', function (value) {
          const { col } = this.parent;
          if (value == null || col == null) return true;
          return value * col <= 1000;
        }),

      col: Yup.number()
        .min(1, 'Column must be at least 1')
        .required('Column is required')
        .test('max-multiply', 'Row × Column cannot be greater than 1000', function (value) {
          const { row } = this.parent;
          if (value == null || row == null) return true;
          return value * row <= 1000;
        }),
      // binCount: Yup.number().when('isCustom', {
      //   is: true,
      //   then: (schema) =>
      //     schema
      //       .min(1, 'Bin count must be at least 1')
      //       .required('Bin count is required')
      //       .test('binCountCheck', function (value = 0) {
      //         const { row = 0, col = 0 } = this.parent;
      //         const maxBins = row * col;
      //         return value <= maxBins
      //           ? true
      //           : this.createError({
      //               message: `Bin count cannot be greater than row * col (${maxBins})`,
      //             });
      //       }),
      //   otherwise: () => Yup.mixed().notRequired(),
      // }),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        const request: RackRequest = {
          ...values,
          wardId: wardId.value,
          info: 'info',
          isCustom: values.isCustom,
        };
        // check if row or col is changed
        if (itemRack) {
          if (itemRack.row !== Number(values.row) || itemRack.col !== Number(values.col)) {
            // check if itemRack items is not zero
            if (itemRack.items && itemRack.items > 0 && !forceUpdate) {
              setShowModalConfirm(true);
              return;
            }
          }
        }

        request.row = Number(values.row);
        request.col = Number(values.col);

        if (itemRack) {
          const dataRes = await updateRack(itemRack.id, request);
          toast.success(`Update rack successfully!`);
          setForceUpdate(false);
          if (dataRes) {
            const rack = await getRack(itemRack.id, orgId);
            if (isEditLayoutRack) {
              fetchItems();
              fetchSensors();
            }

            if (onClickCloseModal) {
              setRefetchTimestamp?.(new Date().getTime());
              onClickCloseModal(rack);
              return;
            }
            cancel(true);
          }
          return;
        }
        await createRack(request);
        toast.success(`Add rack successfully!`);
        if (state.page !== 1) {
          cancel();
          onChangeParams({ page: 1 });
          return;
        }
        cancel(true);
      } catch (error: any) {
        getResponseError(error);
      } finally {
        setSubmitting(true);
      }
    },
  });
  const cancel = (withRefresh?: boolean) => {
    if (!isUndefined(onClickCloseModal)) {
      onClickCloseModal();
      return;
    }
    if (withRefresh) {
      refetch();
    }
    setItemForUpdate(emptyListViewProvider);
  };

  const handleChangeTypeRack = (rack: RackFormType) => {
    if (isNull(itemRack)) {
      rackType.setValue(rack.value);
      formik.setFieldValue('isCustom', rack.value === RackType['Custom rack']);
    }
  };

  return (
    <React.Fragment>
      <form id="kt_modal_add_user_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <FormInput
          label="Rack name"
          placeholder="Rack name"
          formik={formik}
          field="name"
          isRequired
          rootClass="w-100"
        />
        <div style={{ marginBottom: '24px' }}>
          <ItemType>Type</ItemType>
          <div className="d-flex">
            {RACK_TYPE.map((rack, index) => {
              const checked = Boolean(Number(rackType.value) === rack.value);
              return (
                <div className="me-10">
                  <RadioInput
                    key={index}
                    className="form-check-input me-1"
                    type="radio"
                    value={rack.value}
                    checked={checked}
                    onChange={() => handleChangeTypeRack(rack)}
                    label={rack.title}
                    disabled={!isNull(itemRack)}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {typeCustom ? (
          <>
            <FormInput
              label="Total rows"
              placeholder="At least 1 row"
              formik={formik}
              field="row"
              typeInput="number"
              rootClass="w-100"
              isRequired
            />
            <FormInput
              label="Total columns"
              placeholder="At least 1 column"
              formik={formik}
              field="col"
              typeInput="number"
              rootClass="w-100"
              isRequired
            />

            {/* <FormInput
              label="Total bins"
              placeholder="At least 1 bin"
              formik={formik}
              field="binCount"
              typeInput="number"
              rootClass="w-100"
              isRequired
            /> */}
          </>
        ) : null}

        <div className="d-flex justify-content-between">
          <WardCard>
            <FormSelect
              label="Assign to Location"
              handleChange={(select) => wardId.setValue(select.value)}
              isDisable={hasItemAssigned}
              optionsSelect={wards.map((el) => {
                return {
                  value: Number(el.id),
                  title: el.name,
                };
              })}
              isRequired
              activeSelect={wardId.value}
              fieldActive="value"
              fieldSelect="title"
              placementCustom="bottom-start"
              styleSelectCard={{
                width: 584,
              }}
            />
          </WardCard>
        </div>

        <ActionButton
          onCancel={() => cancel()}
          disableButton={formik.isSubmitting || !formik.isValid || !formik.touched || !wardId.value}
          isSubmitting={formik.isSubmitting}
          labelButtonSubmit={itemRack ? 'Save' : 'Add'}
        />
        {showModalConfirm && (
          <ConfirmationModal
            title="Warning"
            label="This rack has assigned items, the changes will clear all items in this rack. Are you sure you want to continue?"
            submitText="Save"
            onCloseModal={() => {
              setShowModalConfirm(false);
              return;
            }}
            onSubmit={() => {
              setForceUpdate(true);
              return;
            }}
          />
        )}
      </form>
      {formik.isSubmitting && <LoadingCard />}
    </React.Fragment>
  );
};
export default FormRackModal;
